import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Loader2, ChevronDown, ChevronRight, UserCircle2,
  ShieldAlert, Lock,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useDataLabel } from '@/lib/dataI18n';

/**
 * Admin/Consultant response viewer.
 *
 * Renders the submitted answers for `questionnaireId` (optionally filtered
 * to a single `userId`) with two privacy filters already applied by the
 * server:
 *
 *   - HR-only questions are stripped from `version.questions` for non-HR
 *     callers and `hr_only_visible` is false — UI shows a small disclosure
 *     so the operator understands content is filtered.
 *   - Anonymous-tagged questions arrive in `anonymous_answers` (qid → list
 *     of answer payloads with no respondent metadata of any kind).
 */
export default function ResponseViewer({ questionnaireId, userId }) {
  const { t, i18n } = useTranslation();
  const tData = useDataLabel();
  const isEN = (i18n.language || '').toLowerCase().startsWith('en');
  const tx = (es, en) => (isEN && en ? en : (es || en || ''));

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    const params = { questionnaire_id: questionnaireId };
    if (userId) params.user_id = userId;
    api.get('/admin/responses', { params })
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => {
        if (alive) setError(e?.response?.data?.detail || e.message);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [questionnaireId, userId]);

  if (loading) {
    return (
      <Card className="p-8 flex items-center justify-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('responseViewer.loading')}
      </Card>
    );
  }
  if (error) {
    return (
      <Card className="p-6 border-destructive/40 bg-destructive/5 text-sm">
        {error}
      </Card>
    );
  }
  if (!data || !data.version) {
    return (
      <Card className="p-8 text-center text-sm text-muted-foreground">
        {t('responseViewer.empty')}
      </Card>
    );
  }

  const { version, respondents, answers_by_user, anonymous_answers, hr_only_visible } = data;
  const sections = (version.sections || []).slice().sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0),
  );
  const visibleQuestions = version.questions || [];
  const anonQids = new Set(Object.keys(anonymous_answers || {}));

  // Build a fast lookup map for option label rendering
  const optionLabel = (q, optId) => {
    const o = (q.options || []).find((x) => x.id === optId);
    if (!o) return optId;
    return tx(o.label, o.label_en);
  };

  if (respondents.length === 0) {
    return (
      <Card className="p-8 text-center text-sm text-muted-foreground" data-testid="response-viewer-empty">
        {t('responseViewer.noSubmissions')}
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="response-viewer">
      {/* Privacy banner */}
      <div className="flex flex-wrap items-center gap-2">
        <Badge
          variant="outline"
          className="text-xs gap-1 bg-info/10 text-info border-info/20"
          data-testid="response-viewer-respondents-count"
        >
          <UserCircle2 className="h-3 w-3" />
          {t('responseViewer.respondents', { n: respondents.length })}
        </Badge>
        {hr_only_visible ? (
          <Badge
            variant="outline"
            className="text-xs gap-1 bg-warning/10 text-warning border-warning/20"
            data-testid="response-viewer-hr-visible-true"
          >
            <Lock className="h-3 w-3" /> {t('responseViewer.hrVisible')}
          </Badge>
        ) : (
          <Badge
            variant="outline"
            className="text-xs gap-1 bg-muted text-muted-foreground"
            data-testid="response-viewer-hr-visible-false"
          >
            <Lock className="h-3 w-3" /> {t('responseViewer.hrHidden')}
          </Badge>
        )}
        {anonQids.size > 0 && (
          <Badge
            variant="outline"
            className="text-xs gap-1 bg-accent/10 text-accent border-accent/20"
            data-testid="response-viewer-anon-flag"
          >
            <ShieldAlert className="h-3 w-3" />
            {t('responseViewer.anonInPlay', { n: anonQids.size })}
          </Badge>
        )}
      </div>

      {sections.map((s) => {
        const sectionQs = visibleQuestions.filter((q) => q.section_id === s.id);
        if (sectionQs.length === 0) return null;
        const sid = s.id;
        const isOpen = expanded[sid] ?? true;
        return (
          <Card key={sid} className="p-0 shadow-card overflow-hidden" data-testid={`response-section-${sid}`}>
            <button
              type="button"
              onClick={() => setExpanded((e) => ({ ...e, [sid]: !isOpen }))}
              className="w-full px-5 py-4 flex items-center justify-between border-b border-border hover:bg-muted/30 transition-colors text-left"
            >
              <div>
                <h3 className="font-display font-bold tracking-tight">
                  {tx(s.name, s.name_en)}
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t('responseViewer.qInSection', { n: sectionQs.length })}
                </p>
              </div>
              {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
            {isOpen && (
              <div className="divide-y divide-border">
                {sectionQs.map((q, qi) => (
                  <QuestionBlock
                    key={q.id}
                    q={q}
                    qIndex={qi + 1}
                    isAnon={anonQids.has(q.id)}
                    respondents={respondents}
                    answersByUser={answers_by_user[q.id] || {}}
                    anonAnswers={anonymous_answers[q.id] || []}
                    tx={tx}
                    t={t}
                    tData={tData}
                    optionLabel={optionLabel}
                  />
                ))}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function QuestionBlock({
  q, qIndex, isAnon, respondents, answersByUser, anonAnswers,
  tx, t, optionLabel,
}) {
  return (
    <div className="px-5 py-4" data-testid={`response-question-${q.id}`}>
      <div className="flex items-start gap-2 mb-3 flex-wrap">
        <span className="h-6 w-6 shrink-0 rounded-md bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">
          {qIndex}
        </span>
        <p className="flex-1 min-w-0 text-sm font-medium leading-relaxed">
          {tx(q.prompt, q.prompt_en)}
        </p>
        {isAnon && (
          <Badge
            variant="outline"
            className="text-xs gap-1 bg-accent/10 text-accent border-accent/20"
            data-testid={`response-q-anon-badge-${q.id}`}
          >
            <ShieldAlert className="h-3 w-3" /> {t('responseViewer.tagAnonymous')}
          </Badge>
        )}
      </div>

      {isAnon ? (
        // Pooled anonymous answers — no identity rendered
        anonAnswers.length === 0 ? (
          <p className="text-xs italic text-muted-foreground ml-8">
            {t('responseViewer.noAnswersYet')}
          </p>
        ) : (
          <ul className="space-y-2 ml-8" data-testid={`response-anon-list-${q.id}`}>
            {anonAnswers.map((a, idx) => (
              <li
                key={idx}
                className="rounded-lg border border-accent/20 bg-accent/5 p-3"
                data-testid={`response-anon-item-${q.id}-${idx}`}
              >
                {a.text ? (
                  <p className="text-sm whitespace-pre-wrap break-words">{a.text}</p>
                ) : (
                  <p className="text-sm text-muted-foreground italic">
                    {(a.option_ids || []).map((oid) => optionLabel(q, oid)).join(', ') || '—'}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )
      ) : (
        // Per-respondent rows — identity attached
        respondents.length === 0 ? (
          <p className="text-xs italic text-muted-foreground ml-8">
            {t('responseViewer.noAnswersYet')}
          </p>
        ) : (
          <div className="space-y-2 ml-8">
            {respondents.map((r) => {
              const a = answersByUser[r.user?.id];
              if (!a) return null;
              const initials = (r.user?.name || r.user?.email || '?')
                .split(' ')
                .map((p) => p[0])
                .filter(Boolean)
                .slice(0, 2)
                .join('')
                .toUpperCase();
              return (
                <div
                  key={r.user?.id}
                  className="flex items-start gap-2.5 rounded-lg border border-border p-3"
                  data-testid={`response-item-${q.id}-${r.user?.id}`}
                >
                  <Avatar className="h-7 w-7 shrink-0">
                    <AvatarFallback className="bg-primary/10 text-primary text-xs font-bold">
                      {initials || '?'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium">{r.user?.name || r.user?.email || '—'}</p>
                    {a.text ? (
                      <p className="text-sm mt-0.5 whitespace-pre-wrap break-words">{a.text}</p>
                    ) : (
                      <p className="text-sm mt-0.5 text-muted-foreground">
                        {(a.option_ids || []).map((oid) => optionLabel(q, oid)).join(', ') || '—'}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}
